/**
 * Multi-step public intake → quote flow (consumer-funnel §5.1, P2-1).
 *
 * Evolves the old 3-field intake into a real front door:
 *   1. FIELDS   — the TENANT's configured question set (falls back to the stock
 *                 set) collects the matter.
 *   2. SUMMARY  — a plain-English structured summary the prospect can correct.
 *   3. QUOTE    — a visible flat-fee estimate + itemized covered / NOT-covered
 *                 scope + the 24h SLA, framed as an estimate a licensed attorney
 *                 confirms within one business day (pre-assignment, non-binding).
 *   4. SAVED    — confirm/save with save-and-resume (a saved {id,email} in
 *                 localStorage lets a slow reader come back to their quote).
 *
 * Tenant-branded per §0.5: the AI assistant is named + surfaced at the tenant's
 * DISCLOSURE LEVEL (never "ELARIA"/"the service" for a white-label tenant), and
 * the palette comes from the surrounding <TenantThemeProvider>. The API is a
 * sibling repo shipping in parallel, so every server-shaped read degrades: no
 * quote → a "your attorney confirms the fee" line; no summary → synthesized
 * locally from the answers.
 */
import { useEffect, useMemo, useState, type MutableRefObject } from 'react';
import {
  captureLead, fetchLeadQuote,
  type LeadFields, type LeadQuote, type StructuredSummary,
} from '@/lib/api';
import { fetchRateCard, pickAnchor } from '@/lib/rateCard';
import { useTenant, type IntakeQuestion } from '@/lib/tenant';
import modal from './Modal.module.css';
import styles from './IntakeFlow.module.css';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RESUME_KEY = 'avowal:intake:saved';
const KNOWN = new Set(['email', 'jurisdiction', 'matterType', 'summary']);

type Step = 'fields' | 'summary' | 'quote' | 'saved';

/** Normalized quote for display — merges a server quote or a rate-card anchor. */
interface DisplayQuote {
  legalLowCents?: number;
  legalHighCents?: number;
  legalAnchorCents?: number;
  platformFeeCents?: number;
  currency: string;
  covered: string[];
  notCovered: string[];
  scopeNote?: string;
  slaHours: number;
}

/** Shared handle so the hosting modal can persist an abandoned lead on close. */
export interface IntakeFlowHandle {
  fields: LeadFields;
  done: boolean;
}

function usd(cents: number | null | undefined, currency = 'USD'): string | null {
  if (cents === null || cents === undefined) return null;
  const n = Number(cents);
  if (!Number.isFinite(n)) return null;
  return (n / 100).toLocaleString(undefined, { style: 'currency', currency, maximumFractionDigits: 0 });
}

function feeRange(q: DisplayQuote): string | null {
  const lo = usd(q.legalLowCents, q.currency);
  const hi = usd(q.legalHighCents, q.currency);
  if (lo && hi) return lo === hi ? lo : `${lo}–${hi}`;
  return usd(q.legalAnchorCents, q.currency) ?? lo ?? hi;
}

function buildDisplayQuote(quote: LeadQuote | undefined): DisplayQuote | null {
  if (!quote) return null;
  const anchorCents = quote.legalFeeAnchorCents ?? quote.totalCents;
  const hasFee = quote.legalFeeLowCents != null || quote.legalFeeHighCents != null || anchorCents != null;
  const hasScope = !!(quote.scope?.covered?.length || quote.scope?.notCovered?.length);
  if (!hasFee && !hasScope) return null;
  return {
    legalLowCents: quote.legalFeeLowCents,
    legalHighCents: quote.legalFeeHighCents,
    legalAnchorCents: anchorCents,
    platformFeeCents: quote.platformFeeCents,
    currency: quote.currency || 'USD',
    covered: quote.scope?.covered ?? [],
    notCovered: quote.scope?.notCovered ?? [],
    slaHours: quote.slaHours ?? 24,
  };
}

export default function IntakeFlow({
  questions, handleRef, onClose,
}: {
  questions: IntakeQuestion[];
  handleRef: MutableRefObject<IntakeFlowHandle>;
  onClose: () => void;
}) {
  const { displayName, clientAssistantName } = useTenant();
  const [step, setStep] = useState<Step>('fields');
  const [values, setValues] = useState<Record<string, string>>({});
  const [phase, setPhase] = useState<'idle' | 'submitting' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [summary, setSummary] = useState<StructuredSummary | null>(null);
  const [quote, setQuote] = useState<DisplayQuote | null>(null);
  const [resume, setResume] = useState<{ id: string; email: string } | null>(null);

  // Offer save-and-resume when a saved quote exists (a fatigued reader who left).
  useEffect(() => {
    try {
      const raw = localStorage.getItem(RESUME_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw) as { id?: string; email?: string };
      if (saved?.id && saved?.email) setResume({ id: saved.id, email: saved.email });
    } catch { /* ignore */ }
  }, []);

  const email = (values.email ?? '').trim();

  // Keep the shared handle current so the modal's close effect can persist a
  // partial lead (abandoned-lead capture is retained from the old modal).
  const collectFields = (): LeadFields => {
    const extra: Record<string, string> = {};
    for (const [k, v] of Object.entries(values)) if (!KNOWN.has(k) && v?.trim()) extra[k] = v.trim();
    return {
      email,
      jurisdiction: values.jurisdiction?.trim() || undefined,
      matterType: values.matterType?.trim() || undefined,
      summary: values.summary?.trim() || undefined,
      extra: Object.keys(extra).length ? extra : undefined,
    };
  };
  useEffect(() => {
    handleRef.current.fields = collectFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  const requiredMissing = useMemo(() => {
    for (const q of questions) {
      if (!q.required) continue;
      const v = (values[q.name] ?? '').trim();
      if (!v) return true;
      if (q.type === 'email' && !EMAIL_RE.test(v)) return true;
    }
    return false;
  }, [questions, values]);

  const setField = (name: string, v: string) => setValues((p) => ({ ...p, [name]: v }));

  const markDone = () => { handleRef.current.done = true; };

  const submitFields = async () => {
    if (phase === 'submitting' || requiredMissing) return;
    setPhase('submitting');
    setErrorMsg('');
    const fields = collectFields();
    const r = await captureLead(fields, true);
    if (!r.ok) {
      setPhase('error');
      setErrorMsg(`Couldn't submit (${r.error}). Email hello@${(displayName || 'avowal').toLowerCase().replace(/\s+/g, '')}.ai instead.`);
      return;
    }
    markDone();
    setSummary(
      r.structuredSummary ?? {
        facts: fields.summary ?? '',
        matterType: fields.matterType,
        jurisdiction: fields.jurisdiction,
      },
    );
    // Prefer the server quote; else anchor a client-side estimate off the
    // published rate card; else leave it null (the quote step degrades).
    let dq = buildDisplayQuote(r.quote);
    if (!dq) {
      const rc = await fetchRateCard();
      if (rc.ok) {
        const anchor = pickAnchor(rc.anchors, fields.matterType, fields.jurisdiction);
        if (anchor) {
          dq = {
            legalLowCents: anchor.anchorLegalFeeLowCents,
            legalHighCents: anchor.anchorLegalFeeHighCents,
            platformFeeCents: anchor.platformFeeCents,
            currency: anchor.currency || 'USD',
            covered: [],
            notCovered: [],
            scopeNote: anchor.scope || undefined,
            slaHours: anchor.slaHours ?? 24,
          };
        }
      }
    }
    setQuote(dq);
    // Persist for save-and-resume (id from the server; email is the prospect's).
    if (r.id && email) {
      try { localStorage.setItem(RESUME_KEY, JSON.stringify({ id: r.id, email })); } catch { /* ignore */ }
    }
    setPhase('idle');
    setStep('summary');
  };

  const doResume = async () => {
    if (!resume) return;
    setPhase('submitting');
    setErrorMsg('');
    const r = await fetchLeadQuote(resume.id, resume.email);
    setPhase('idle');
    if (!r.ok) {
      // Stale/expired saved quote — clear it and let them start fresh.
      try { localStorage.removeItem(RESUME_KEY); } catch { /* ignore */ }
      setResume(null);
      return;
    }
    setValues((p) => ({ ...p, email: resume.email }));
    if (r.structuredSummary) setSummary(r.structuredSummary);
    setQuote(buildDisplayQuote(r.quote));
    setStep('quote');
  };

  const dismissResume = () => {
    try { localStorage.removeItem(RESUME_KEY); } catch { /* ignore */ }
    setResume(null);
  };

  const stateName = summary?.jurisdiction || values.jurisdiction?.trim() || null;
  const attorneyLine = stateName
    ? `A licensed ${stateName} attorney confirms this within one business day.`
    : 'A licensed attorney confirms this within one business day.';

  // ── Step: SAVED ────────────────────────────────────────────────────────────
  if (step === 'saved') {
    return (
      <div className={styles.saved}>
        <div className={styles.savedIcon}>✓</div>
        <div className={styles.savedTitle}>Your quote is saved</div>
        <p className={styles.savedText}>
          {clientAssistantName ? `${clientAssistantName} has your intake` : `${displayName} has your intake`}, and a licensed
          attorney will confirm your fixed quote within one business day. We saved it on this
          device — reopen this window any time to pick up right where you left off. Nothing is
          signed, paid, or filed until you approve it.
        </p>
        <div className={styles.backRow}>
          <button type="button" className={styles.ghostBtn} onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

  // ── Step: QUOTE ────────────────────────────────────────────────────────────
  if (step === 'quote') {
    return (
      <>
        <StepBar step="quote" />
        <div className={modal.eyebrow}>/ your quote</div>
        <h3 className={modal.title}>A fixed price, in writing</h3>
        <p className={modal.sub}>
          Here's an up-front flat-fee estimate for what you described — and exactly what it
          does and doesn't cover. No surprises.
        </p>

        {quote ? (
          <div className={styles.quoteCard}>
            <div className={styles.quoteEstimate}>Estimate · not yet binding</div>
            <div className={styles.quoteFee}>
              <span className={styles.quoteFeeLabel}>Legal fee — paid to your attorney</span>
              <span className={styles.quoteFeeVal}>{feeRange(quote) ?? 'Confirmed with you'}</span>
            </div>
            {quote.platformFeeCents != null && (
              <div className={styles.quotePlatform}>
                <span>Platform software fee — billed to your attorney, not you</span>
                <span className={styles.quotePlatformVal}>{usd(quote.platformFeeCents, quote.currency)}</span>
              </div>
            )}
            <div className={styles.quoteNote}>
              You pay your attorney directly for the legal work. {displayName} never takes a cut of
              your legal fee.
            </div>
          </div>
        ) : (
          <div className={styles.quoteCard}>
            <div className={styles.quoteEstimate}>Estimate · not yet binding</div>
            <div className={styles.quoteNote}>
              A licensed attorney will confirm a fixed, written quote for your matter within one
              business day — before anything is signed or paid.
            </div>
          </div>
        )}

        {quote && (quote.covered.length > 0 || quote.notCovered.length > 0 || quote.scopeNote) && (
          <div className={styles.scopeBlock}>
            {quote.covered.length > 0 && (
              <div className={styles.scopeCol}>
                <div className={`${styles.scopeHead} ${styles.scopeHeadCovered}`}>What's covered</div>
                <ul className={styles.scopeList}>
                  {quote.covered.map((c, i) => (
                    <li key={i} className={`${styles.scopeItem} ${styles.scopeItemCovered}`}>{c}</li>
                  ))}
                </ul>
              </div>
            )}
            {quote.notCovered.length > 0 && (
              <div className={styles.scopeCol}>
                <div className={`${styles.scopeHead} ${styles.scopeHeadNot}`}>Not covered</div>
                <ul className={styles.scopeList}>
                  {quote.notCovered.map((c, i) => (
                    <li key={i} className={`${styles.scopeItem} ${styles.scopeItemNot}`}>{c}</li>
                  ))}
                </ul>
              </div>
            )}
            {quote.covered.length === 0 && quote.notCovered.length === 0 && quote.scopeNote && (
              <p className={styles.scopeNote}>{quote.scopeNote}</p>
            )}
          </div>
        )}

        <div className={styles.sla}>
          <span className={styles.slaDot} aria-hidden="true" />
          A human replies within {quote?.slaHours ?? 24} hours.
        </div>

        <p className={styles.confirmLine}>{attorneyLine} Save your quote and we'll pick up right here.</p>

        <button type="button" className={modal.submit} onClick={() => setStep('saved')}>
          Save my quote →
        </button>
        <div className={styles.backRow}>
          <button type="button" className={styles.linkBtn} onClick={() => setStep('summary')}>← Back to your summary</button>
        </div>
      </>
    );
  }

  // ── Step: SUMMARY ──────────────────────────────────────────────────────────
  if (step === 'summary') {
    return (
      <>
        <StepBar step="summary" />
        <div className={modal.eyebrow}>/ does this look right?</div>
        <h3 className={modal.title}>Here's what we heard</h3>
        <p className={modal.sub}>
          {clientAssistantName ? `${clientAssistantName} put your matter in plain English.` : 'Here is your matter in plain English.'}{' '}
          Fix anything that's off before we price it.
        </p>

        <div className={styles.summaryCard}>
          <div className={styles.summaryLabel}>Your matter, in plain English</div>
          <textarea
            className={styles.summaryFacts}
            value={summary?.facts ?? ''}
            onChange={(e) => setSummary((s) => ({ ...(s ?? {}), facts: e.target.value }))}
            placeholder="A short, plain-English description of what you need."
          />
          <div className={styles.summaryTags}>
            {summary?.matterType && <span className={styles.tag}>Type: <em>{summary.matterType}</em></span>}
            {summary?.jurisdiction && <span className={styles.tag}>Where: <em>{summary.jurisdiction}</em></span>}
            {summary?.county && <span className={styles.tag}>County: <em>{summary.county}</em></span>}
          </div>
          {summary?.fields && summary.fields.length > 0 && (
            <dl className={styles.summaryFields}>
              {summary.fields.map((f) => (
                <div key={f.id} className={styles.summaryFieldRow}>
                  <dt className={styles.summaryFieldLabel}>{f.label}</dt>
                  <dd className={styles.summaryFieldValue}>{f.value}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>

        <button type="button" className={modal.submit} onClick={() => setStep('quote')}>
          This looks right — show my quote →
        </button>
        <div className={styles.backRow}>
          <button type="button" className={styles.linkBtn} onClick={() => setStep('fields')}>← Edit my answers</button>
        </div>
      </>
    );
  }

  // ── Step: FIELDS ───────────────────────────────────────────────────────────
  return (
    <>
      <StepBar step="fields" />
      <div className={modal.eyebrow}>/ tell us about your matter</div>
      <h3 className={modal.title}>Start with the basics</h3>
      <p className={modal.sub}>
        {clientAssistantName
          ? `${clientAssistantName} reviews your intake first — you'll get a fixed-fee quote and a response within one business day.`
          : `Your intake goes straight to the office — you'll get a fixed-fee quote and a response within one business day.`}
      </p>

      {resume && (
        <div className={styles.resume}>
          <div className={styles.resumeText}>
            You have a saved quote on this device. Want to pick up where you left off?
          </div>
          <div className={styles.resumeActions}>
            <button type="button" className={modal.submit} style={{ width: 'auto', padding: '0.5rem 1rem' }} onClick={doResume} disabled={phase === 'submitting'}>
              {phase === 'submitting' ? 'Loading…' : 'Resume my quote →'}
            </button>
            <button type="button" className={styles.linkBtn} onClick={dismissResume}>Start over</button>
          </div>
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); void submitFields(); }}>
        {questions.map((q) => (
          <div className={modal.field} key={q.name}>
            <label htmlFor={`if-${q.name}`}>{q.label}</label>
            {q.type === 'textarea' ? (
              <textarea
                id={`if-${q.name}`} name={q.name} required={q.required}
                placeholder={q.placeholder}
                value={values[q.name] ?? ''}
                onChange={(e) => setField(q.name, e.target.value)}
              />
            ) : q.type === 'select' ? (
              <select
                id={`if-${q.name}`} name={q.name} required={q.required}
                value={values[q.name] ?? ''}
                onChange={(e) => setField(q.name, e.target.value)}
              >
                <option value="" disabled>Select…</option>
                {(q.options ?? []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            ) : (
              <input
                id={`if-${q.name}`} name={q.name} required={q.required}
                type={q.type === 'email' ? 'email' : 'text'}
                autoComplete={q.type === 'email' ? 'email' : undefined}
                placeholder={q.placeholder}
                value={values[q.name] ?? ''}
                onChange={(e) => setField(q.name, e.target.value)}
              />
            )}
            {q.help && <div style={{ fontFamily: 'var(--font-serif)', fontSize: '0.75rem', color: 'var(--text-xdim)', marginTop: '0.3rem' }}>{q.help}</div>}
          </div>
        ))}
        <button type="submit" className={modal.submit} disabled={phase === 'submitting' || requiredMissing}>
          {phase === 'submitting' ? 'Reviewing…' : 'See my quote →'}
        </button>
        {phase === 'error' && <div className={`${modal.msg} ${modal.error}`}>{errorMsg}</div>}
      </form>
    </>
  );
}

function StepBar({ step }: { step: Step }) {
  const order: Step[] = ['fields', 'summary', 'quote'];
  const labels: Record<Step, string> = { fields: 'Your matter', summary: 'Summary', quote: 'Quote', saved: 'Saved' };
  const idx = order.indexOf(step);
  return (
    <div className={styles.steps} aria-hidden="true">
      {order.map((s, i) => (
        <span key={s} style={{ display: 'contents' }}>
          <span className={`${styles.step} ${i === idx ? styles.stepOn : i < idx ? styles.stepDone : ''}`}>
            <span className={styles.stepDot}>{i < idx ? '✓' : i + 1}</span>
            {labels[s]}
          </span>
          {i < order.length - 1 && <span className={styles.stepBar} />}
        </span>
      ))}
    </div>
  );
}
